/* eslint-disable no-nested-ternary */
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { cva, VariantProps } from 'class-variance-authority';
import React from 'react';
import { twMerge } from 'tailwind-merge';

const $select = cva([], {
  variants: {
    variant: {
      primary: [],
      disabled: [],
    },
    sizeVariant: {
      sm: ['text-xs/4'],
      md: ['text-base', 'sm:text-sm'],
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

export interface MultiSelectProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>,
    VariantProps<typeof $select> {
  label?: string;
  orientation?: 'vertical' | 'horizontal';
  sizeVariant?: 'sm' | 'md';
  options: { label: string; value: string | number }[];
  selected: { label: string; value: string | number }[];
  setSelected: (value: { label: string; value: string | number }[]) => void;
  isInvalid?: boolean;
  validationMessage?: string;
  actionLabel?: string;
  onActionClick?: () => void;
}

export function MultiSelect({
  label,
  orientation = 'vertical',
  sizeVariant = 'md',
  options,
  selected,
  setSelected,
  isInvalid = false,
  validationMessage,
  actionLabel,
  onActionClick,
  ...props
}: MultiSelectProps) {
  const handleChange = (option: { label: string; value: string | number }) => {
    if (selected.some((s) => s.value === option.value)) {
      setSelected(selected.filter((s) => s.value !== option.value));
    } else {
      setSelected([...selected, option]);
    }
  };

  // Calculate min width based on longest option label
  const longestLabel = options.reduce(
    (longest, option) =>
      option.label.length > longest.length ? option.label : longest,
    ''
  );
  const approxCharWidth = 0.7; // em, adjust as needed for your font
  const minWidthEm = Math.ceil(longestLabel.length * approxCharWidth) + 3; // +3 for icon/padding

  return (
    <div className='flex flex-col grow'>
      <Listbox value={selected} onChange={() => {}} multiple>
        <div
          className={`${twMerge($select({ sizeVariant }))} flex ${
            orientation === 'vertical' ? 'flex-col' : 'flex-row'
          } grow`}
          {...props}
        >
          <Label
            className={`${
              orientation === `horizontal` ? 'mr-2 mt-2' : ''
            } font-inter text-sm font-medium leading-5 text-gray-900 flex items-baseline gap-2`}
          >
            <span>{label}</span>
            <span className='text-xs font-normal text-gray-500'>
              {selected.length} selected
            </span>
          </Label>
          <div
            className={`relative ${orientation === 'vertical' ? 'mt-2' : ''}`}
          >
            <ListboxButton
              className={twMerge(
                'relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-950 text-base leading-6 font-normal font-inter shadow-sm ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:text-sm h-8',
                isInvalid && 'ring-red-300 border-red-500 focus:ring-red-400'
              )}
            >
              <span className='block truncate'>
                {selected.length > 0
                  ? selected.length <= 3
                    ? selected.map((s) => s.label).join(', ')
                    : `${selected
                        .slice(0, 3)
                        .map((s) => s.label)
                        .join(', ')}, ...`
                  : `Select ${label}`}
              </span>
              <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
                <ChevronUpDownIcon
                  aria-hidden='true'
                  className='h-5 w-5 text-gray-400'
                />
              </span>
            </ListboxButton>

            <ListboxOptions
              modal={false}
              transition
              style={{ minWidth: `${minWidthEm}em` }}
              className='absolute z-10 mt-1 max-h-60 w-max overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm'
            >
              {options.map((option) => {
                const isSelected = selected.some(
                  (s) => s.value === option.value
                );
                return (
                  <ListboxOption
                    key={option.value}
                    value={option}
                    as={React.Fragment}
                  >
                    <div
                      role='option'
                      aria-selected={isSelected}
                      tabIndex={0}
                      className={`
          group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900
          data-[active]:bg-primary data-[active]:text-white
        `}
                      onClick={() => handleChange(option)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleChange(option);
                        }
                      }}
                    >
                      <span
                        className={`block truncate font-normal font-inter ${
                          isSelected ? 'font-semibold' : ''
                        }`}
                      >
                        {option.label}
                      </span>
                      {isSelected && (
                        <span className='absolute inset-y-0 right-0 flex items-center pr-4 text-primary group-data-[selected]:text-white'>
                          <CheckIcon aria-hidden='true' className='h-5 w-5' />
                        </span>
                      )}
                    </div>
                  </ListboxOption>
                );
              })}
              {actionLabel && onActionClick && (
                <>
                  {options.length > 0 && (
                    <div className='border-t border-gray-100' role='separator' />
                  )}
                  <div
                    className='relative cursor-pointer select-none py-2 pl-3 pr-9 text-sm font-medium text-primary hover:bg-blue-50 transition-colors'
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onActionClick();
                    }}
                    role='button'
                    tabIndex={-1}
                  >
                    {actionLabel}
                  </div>
                </>
              )}
            </ListboxOptions>
          </div>
        </div>
      </Listbox>
      {isInvalid && validationMessage && (
        <p id={`${label}-error`} className='mt-2 text-sm text-red-600'>
          {validationMessage}
        </p>
      )}
    </div>
  );
}
